import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import { markdownComponents, formatMarkdownText } from "@/lib/markdownHelpers";
// Checklist-only; callers that need full html-content typography import htmlContent.css themselves.
import "@/lib/lexical/css/CheckList.css";

/**
 * Decodes HTML entities (e.g., &lt;p&gt; -> <p>)
 */
export function decodeHtmlEntities(str: string): string {
  const txt = document.createElement("textarea");
  txt.innerHTML = str;
  return txt.value;
}

/**
 * Helper to check if a string is likely HTML content
 */
export function isLikelyHtml(str: string | null | undefined): boolean {
  if (!str) return false;
  const trimmed = str.trim();
  // Check for common HTML tags/patterns or HTML entities
  const htmlTagPattern = /<\/?[a-z][\s\S]*>/i;
  const htmlEntityPattern = /&[a-z]+;/i;
  return htmlTagPattern.test(trimmed) || htmlEntityPattern.test(trimmed);
}

/**
 * Extracts and cleans HTML content with enhanced cleanup for better formatting
 */
export function extractAndCleanHtml(str: string): string {
  const decoded = /<\/?[a-z][\s\S]*>/i.test(str) ? str : decodeHtmlEntities(str);
  // Remove literal \n
  let cleaned = decoded.replace(/\\n/g, "");

  // Clean up excessive spacing and problematic styling
  cleaned = cleaned
    // Remove empty paragraphs with just <br> tags
    .replace(/<p[^>]*><br\s*\/?><\/p>/gi, "")
    // Remove empty paragraphs
    .replace(/<p[^>]*>\s*<\/p>/gi, "")
    // Remove standalone <br> tags that create excessive spacing
    .replace(/(<\/p>)\s*<p><br\s*\/?>/gi, "$1")
    // Clean up multiple consecutive <br> tags
    .replace(/(<br\s*\/?>){2,}/gi, "<br>")
    // Remove white-space: pre-wrap from spans as it can cause layout issues
    .replace(/style="white-space:\s*pre-wrap;?"/gi, "")
    .replace(/style="white-space:\s*pre-wrap;\s*([^"]+)"/gi, 'style="$1"')
    // Clean up empty style attributes
    .replace(/\s+style=""\s*/gi, " ");

  // Parse as HTML
  const parser = new DOMParser();
  const doc = parser.parseFromString(cleaned, "text/html");

  // Get the content directly from the body
  return doc.body.innerHTML;
}

/**
 * Basic HTML extraction without advanced cleanup (for backward compatibility)
 */
export function extractInnerHtml(str: string): string {
  const decoded = /<\/?[a-z][\s\S]*>/i.test(str) ? str : decodeHtmlEntities(str);
  // Remove literal \n
  const cleaned = decoded.replace(/\\n/g, "");

  // Parse as HTML
  const parser = new DOMParser();
  const doc = parser.parseFromString(cleaned, "text/html");

  // Get the content directly from the body
  return doc.body.innerHTML;
}

function markReadOnlyChecklists(html: string): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  for (const item of Array.from(doc.querySelectorAll('li[role="checkbox"]'))) {
    item.setAttribute("aria-disabled", "true");
  }
  return doc.body.innerHTML;
}

/**
 * Strips HTML tags and collapses whitespace, returning a plain string.
 * Non-HTML input is returned as-is.
 */
export function plainText(html: string): string {
  if (!isLikelyHtml(html)) return html;
  const decoded = decodeHtmlEntities(html);
  return decoded
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Renders content as HTML or Markdown based on content type
 */
export interface RenderContentOptions {
  /** Whether to use enhanced HTML cleanup */
  enhancedCleanup?: boolean;
  /** CSS class name for HTML content wrapper */
  htmlClassName?: string;
}

export function renderContent(content: string | null | undefined, options: RenderContentOptions = {}): JSX.Element {
  const { enhancedCleanup = true, htmlClassName = "html-content" } = options;

  if (!content || content.trim() === "") {
    return <div className="opacity-20">--</div>;
  }

  if (isLikelyHtml(content)) {
    const processedHtml = markReadOnlyChecklists(
      enhancedCleanup ? extractAndCleanHtml(content) : extractInnerHtml(content)
    );

    return <div className={htmlClassName} dangerouslySetInnerHTML={{ __html: processedHtml }} />;
  }

  return (
    <ReactMarkdown components={markdownComponents} rehypePlugins={[rehypeRaw]}>
      {formatMarkdownText(content)}
    </ReactMarkdown>
  );
}
